import { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { useI18n } from '../lib/i18n';

// True on a phone-width viewport. Used to decide which PDF format leads the
// dropdown: Mobile first on phones, A4 first on desktop — and whether the
// options render as an anchored dropdown (desktop) or a bottom sheet (phone).
function useIsPhone() {
  const [phone, setPhone] = useState(() => typeof window !== 'undefined' && window.innerWidth <= 640);
  useEffect(() => {
    const onResize = () => setPhone(window.innerWidth <= 640);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return phone;
}

// Two-option PDF download control shared by the internal Project Analysis page and the
// client share page. Both formats export the same data snapshot — only the layout
// differs (see lib/pdfExport.js).
//   dark=true -> styled for the dark share-page header.
//
// The options panel is rendered in a PORTAL at document.body with a very high z-index
// (above every sticky header, modal, and the share page's sticky bottom WhatsApp bar —
// nothing else in the app goes past z-[90]) so it can never be clipped by an ancestor's
// overflow:hidden (e.g. the share page's gradient header) or sit under another layer.
// On a phone it renders as a full-width bottom sheet instead of a small dropdown, so the
// tap targets are easy to hit.
export default function PdfDownloadButton({ onA4, onMobile, busy, dark = false, disabled = false }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const btnRef = useRef(null);
  const panelRef = useRef(null);
  const phone = useIsPhone();
  const [pos, setPos] = useState(null); // desktop anchored position: {top, right, width}

  // close on outside click/touch/Escape — must also check the portaled panel, since it's
  // no longer a DOM sibling of the button once rendered at document.body.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (btnRef.current?.contains(e.target)) return;
      if (panelRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('touchstart', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('touchstart', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // position the desktop dropdown under the button; recomputed on open/resize/scroll
  // since it's now positioned relative to the viewport, not the button's DOM parent.
  useLayoutEffect(() => {
    if (!open || phone) { setPos(null); return; }
    const compute = () => {
      const r = btnRef.current?.getBoundingClientRect();
      if (!r) return;
      setPos({ top: r.bottom + 8, right: Math.max(8, window.innerWidth - r.right), width: 240 });
    };
    compute();
    window.addEventListener('resize', compute);
    window.addEventListener('scroll', compute, true);
    return () => { window.removeEventListener('resize', compute); window.removeEventListener('scroll', compute, true); };
  }, [open, phone]);

  const A4 = { key: 'a4', label: t('pdf_desktop_a4'), desc: t('pdf_desktop_a4_desc'), run: onA4 };
  const MOBILE = { key: 'mobile', label: t('pdf_mobile'), desc: t('pdf_mobile_desc'), run: onMobile };
  const ordered = phone ? [MOBILE, A4] : [A4, MOBILE];

  const btnCls = dark
    ? 'bg-white/10 hover:bg-white/20 border border-white/20 text-white'
    : 'bg-dark hover:bg-card text-white';

  const options = (big) => ordered.map(o => (
    <button key={o.key} onClick={() => { setOpen(false); o.run(); }}
      className={`w-full text-left px-4 hover:bg-gray-50 active:bg-gray-100 border-b border-gray-50 last:border-0 ${big ? 'py-4' : 'py-3'}`}>
      <p className={`font-medium text-gray-800 ${big ? 'text-base' : 'text-sm'}`}>{o.label}</p>
      <p className="text-xs text-gray-400">{o.desc}</p>
    </button>
  ));

  return (
    <>
      <div className="relative" ref={btnRef}>
        <button onClick={() => setOpen(o => !o)} disabled={busy || disabled}
          className={`px-3 py-2 rounded-xl text-sm font-medium disabled:opacity-40 flex items-center gap-1.5 ${btnCls}`}>
          {busy ? t('generating') : t('download_pdf')}<span className="text-[9px] opacity-70">▼</span>
        </button>
      </div>
      {open && !busy && !disabled && createPortal(
        phone ? (
          // phone: full-width bottom sheet, above the sticky header AND the sticky
          // bottom WhatsApp CTA (both sit at z-40 or below on the share page).
          <div className="fixed inset-0 z-[100]" role="dialog" aria-modal="true">
            <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />
            <div ref={panelRef}
              className="absolute left-0 right-0 bottom-0 bg-white rounded-t-2xl shadow-2xl pb-[max(env(safe-area-inset-bottom),12px)]">
              <div className="mx-auto mt-2 mb-1 h-1 w-10 rounded-full bg-gray-200" />
              <p className="px-4 pt-2 pb-1 text-xs font-semibold text-gray-400 uppercase tracking-wide">{t('download_pdf')}</p>
              {options(true)}
            </div>
          </div>
        ) : pos && (
          <div ref={panelRef} style={{ position: 'fixed', top: pos.top, right: pos.right, width: pos.width }}
            className="z-[100] bg-white rounded-xl shadow-2xl border border-gray-100 overflow-hidden">
            {options(false)}
          </div>
        ),
        document.body
      )}
    </>
  );
}
